<template>
  <div class="page-container" style="display:flex;align-items:center;justify-content:center;">
    <div class="card-panel" style="width:100%;max-width:520px;padding:32px;">
      <h2 class="page-title">SN 验证</h2>
      <el-form :model="form" label-position="top">
        <el-form-item label="SN码">
          <el-input v-model="form.sn" />
          <div class="scan-actions">
            <el-button plain icon="el-icon-camera" @click="openScanner">扫描条形码</el-button>
            <span class="scan-hint">使用摄像头扫描条形码后自动填入 SN 码</span>
          </div>
        </el-form-item>
        <el-form-item label="4位验证码">
          <el-input v-model="form.verifyCode" maxlength="4" />
        </el-form-item>
        <el-button type="primary" style="width:100%;" @click="submit">立即验证</el-button>
      </el-form>
    </div>

    <el-dialog
      custom-class="scanner-dialog"
      title="扫描条形码"
      :visible.sync="scannerVisible"
      :fullscreen="isMobile"
      :width="isMobile ? '100%' : '560px'"
      top="0"
      :append-to-body="true"
      :close-on-click-modal="false"
      @closed="handleScannerClosed"
    >
      <div class="scanner-panel">
        <div class="scanner-header-tip">请将条形码放入取景框，识别成功后会自动填入 SN 码。</div>
        <div class="scanner-frame">
          <div ref="scannerContainer" class="scanner-box"></div>
          <div class="scanner-overlay">
            <div class="scanner-guide"></div>
          </div>
        </div>
        <div class="scanner-actions">
          <el-button @click="closeScanner">取消</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Quagga from '@ericblade/quagga2'
import { verifySN } from '../../api/verify'

export default {
  data() {
    return {
      form: {
        sn: this.$route.query.sn || '',
        verifyCode: this.$route.query.code || '',
      },
      scannerVisible: false,
      scannerActive: false,
      isMobile: window.innerWidth <= 768,
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.stopScanner()
  },
  methods: {
    normalizeVerifyCode(code) {
      return String(code || '').trim().padStart(4, '0')
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 768
    },
    async openScanner() {
      if (!navigator.mediaDevices?.getUserMedia) {
        this.$message.error('当前浏览器不支持摄像头扫码')
        return
      }
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        this.$message.error('摄像头扫码需要在 HTTPS 或 localhost 环境下使用')
        return
      }
      this.scannerVisible = true
      await this.$nextTick()
      this.startScanner()
    },
    closeScanner() {
      this.scannerVisible = false
    },
    handleScannerClosed() {
      this.stopScanner()
    },
    startScanner() {
      if (this.scannerActive || !this.$refs.scannerContainer) {
        return
      }
      this.$refs.scannerContainer.innerHTML = ''
      Quagga.init({
        inputStream: {
          type: 'LiveStream',
          target: this.$refs.scannerContainer,
          constraints: {
            facingMode: 'environment',
          },
        },
        locator: {
          patchSize: 'medium',
          halfSample: true,
        },
        decoder: {
          readers: ['code_128_reader', 'code_39_reader', 'ean_reader', 'ean_8_reader'],
        },
        locate: true,
      }, (error) => {
        if (error) {
          this.$message.error('无法启动摄像头扫码，请检查权限')
          return
        }
        Quagga.offDetected(this.onDetected)
        Quagga.onDetected(this.onDetected)
        Quagga.start()
        this.scannerActive = true
      })
    },
    stopScanner() {
      Quagga.offDetected(this.onDetected)
      if (this.scannerActive) {
        Quagga.stop()
      }
      this.scannerActive = false
      if (this.$refs.scannerContainer) {
        this.$refs.scannerContainer.innerHTML = ''
      }
    },
    onDetected(result) {
      const code = result?.codeResult?.code
      if (!code) {
        return
      }
      this.form.sn = code.trim()
      this.$message.success(`已识别 SN：${this.form.sn}`)
      this.closeScanner()
    },
    async submit() {
      const clientId = localStorage.getItem('clientId') || `client-${Math.random().toString(36).slice(2, 10)}`
      const verifyCode = this.normalizeVerifyCode(this.form.verifyCode)
      this.form.verifyCode = verifyCode
      localStorage.setItem('clientId', clientId)
      try {
        const { data } = await verifySN({
          sn: this.form.sn,
          verifyCode,
          clientId,
        })
        this.$router.push({ path: '/verify-result', query: data })
      } catch (error) {
        const payload = error.response?.data || {}
        if ((error.response?.status === 404 || error.response?.status === 400) && (payload.reason || payload.message || '').includes('验证码')) {
          this.$message.error(payload.reason || payload.message || '验证码错误')
          return
        }
        this.$router.push({
          path: '/verify-result',
          query: {
            ...payload,
            sn: this.form.sn,
            verifyCode,
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.scan-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.scan-hint {
  color: #909399;
  font-size: 12px;
}

.scanner-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.scanner-header-tip {
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
  text-align: center;
}

.scanner-frame {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
}

.scanner-box {
  width: 100%;
  min-height: 320px;
  background: #000;
}

.scanner-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.scanner-guide {
  width: min(78vw, 320px);
  height: 110px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.28);
}

.scanner-actions {
  display: flex;
  justify-content: center;
}

:deep(.scanner-dialog) {
  border-radius: 18px;
  overflow: hidden;
}

:deep(.scanner-dialog .el-dialog__body) {
  padding: 16px;
}

@media (max-width: 768px) {
  .page-container {
    align-items: stretch !important;
  }

  .scanner-panel {
    min-height: calc(100vh - 120px);
    justify-content: space-between;
  }

  .scanner-header-tip {
    font-size: 14px;
  }

  .scanner-frame {
    flex: 1;
    min-height: 52vh;
    border-radius: 0;
    margin: 0 -16px;
  }

  .scanner-box {
    min-height: 52vh;
  }

  .scanner-guide {
    width: calc(100vw - 56px);
    max-width: 360px;
    height: 96px;
  }

  .scanner-actions {
    padding-bottom: env(safe-area-inset-bottom);
  }

  :deep(.scanner-dialog) {
    border-radius: 0;
  }

  :deep(.scanner-dialog .el-dialog__header) {
    padding: 14px 16px;
  }

  :deep(.scanner-dialog .el-dialog__body) {
    padding: 12px 16px 16px;
  }
}
</style>
